<template>
  <div
    v-if="modelValue"
    class="modal modal-blur fade show"
    style="display: block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ title }}</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            :disabled="loading"
            @click="handleClose"
          />
        </div>
        <div class="modal-body">
          <slot />
        </div>
        <div class="modal-footer">
          <button
            v-if="showSecondaryButton"
            type="button"
            class="btn"
            :class="secondaryButtonClass"
            :disabled="loading"
            @click="handleSecondary"
          >
            {{ secondaryButtonText }}
          </button>
          <button
            v-if="showPrimaryButton"
            type="button"
            class="btn"
            :class="primaryButtonClass"
            :disabled="loading || primaryButtonDisabled"
            @click="handlePrimary"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
            {{ primaryButtonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

interface Props {
  loading?: boolean;
  modelValue: boolean;
  primaryButtonClass?: string;
  primaryButtonDisabled?: boolean;
  primaryButtonText?: string;
  secondaryButtonClass?: string;
  secondaryButtonText?: string;
  showPrimaryButton?: boolean;
  showSecondaryButton?: boolean;
  title: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  primaryButtonClass: 'btn-primary',
  primaryButtonDisabled: false,
  primaryButtonText: 'Confirmar',
  secondaryButtonClass: 'btn-secondary',
  secondaryButtonText: 'Cancelar',
  showPrimaryButton: true,
  showSecondaryButton: true,
});

const emit = defineEmits<{
  'primary-action': [];
  'secondary-action': [];
  'update:modelValue': [value: boolean];
}>();

const handleClose = () => {
  if (props.loading) return;
  emit('update:modelValue', false);
};

const handlePrimary = () => {
  emit('primary-action');
};

const handleSecondary = () => {
  emit('secondary-action');
};
</script>

<style scoped lang="scss">
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  max-width: 500px;
}

.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem;

  .modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }

  .btn-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6c757d;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
      color: #495057;
    }

    &::before {
      content: '×';
      font-size: 1.5rem;
      line-height: 1;
    }
  }
}

.modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  align-items: center;

  .btn {
    min-width: 100px;
    font-weight: 500;
    border-radius: 6px;
    padding: 0.5rem 1rem;
    transition: all 0.2s ease;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.btn-primary {
      background-color: #0d6efd;
      border-color: #0d6efd;
      color: white;

      &:hover:not(:disabled) {
        background-color: #0b5ed7;
        border-color: #0a58ca;
      }
    }

    &.btn-secondary {
      background-color: #6c757d;
      border-color: #6c757d;
      color: white;

      &:hover:not(:disabled) {
        background-color: #5c636a;
        border-color: #565e64;
      }
    }

    &.btn-danger {
      background-color: #dc3545;
      border-color: #dc3545;
      color: white;

      &:hover:not(:disabled) {
        background-color: #c82333;
        border-color: #bd2130;
      }
    }

    &.btn-outline-secondary {
      background-color: transparent;
      border-color: #6c757d;
      color: #6c757d;

      &:hover:not(:disabled) {
        background-color: #6c757d;
        color: white;
      }
    }
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    margin: 1rem;
    max-width: calc(100% - 2rem);
  }

  .modal-footer {
    flex-direction: column-reverse;
    gap: 0.5rem;

    .btn {
      width: 100%;
    }
  }
}
</style>
